/**
 * MusicResult - Audio player, timed lyrics, metadata, and download button.
 */

import { memo, useState } from 'react';
import { Download, FolderOpen, RotateCcw, Music2, Film } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../../components/Toast';
import { downloadFile } from '@/utils/downloadProxy';
import { isDesktopApp } from '@/utils/apiMode';
import { openVideoFolder as electronOpenFolder } from '@/utils/electronBridge';
import type { MusicJobResult } from '../../services/polling/musicPollAdapter';

interface MusicResultProps {
  result: MusicJobResult;
  onReset: () => void;
  onCreateVideoClip?: (result: MusicJobResult) => void;
}

export const MusicResult = memo(function MusicResult({
  result,
  onReset,
  onCreateVideoClip,
}: MusicResultProps) {
  const { t } = useTranslation();
  const { toasts, showToast, removeToast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  // Desktop app: the file is already saved locally, so show "Show in Folder"
  // instead of triggering a browser download (which in Electron surfaces a
  // confusing native `blob:file://...` notification instead of a real file).
  const isDesktop = isDesktopApp();
  const showOpenFolder = isDesktop && !!result.audioPath;

  const handleDownload = async () => {
    if (!result.audioUrl || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadFile(result.audioUrl, `music-${Date.now()}.mp3`);
    } catch {
      window.open(result.audioUrl, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenFolder = async () => {
    if (!result.audioPath) {
      showToast('error', t('results.openFolder.errorTitle'), t('results.openFolder.noLocalPath'));
      return;
    }
    const success = await electronOpenFolder(result.audioPath);
    if (!success) {
      showToast('error', t('results.openFolder.errorTitle'), t('results.openFolder.errorMessage'));
    }
  };

  return (
    <div className="space-y-4">
      {/* Audio player card */}
      <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-4 sm:p-6 space-y-4">
        <div className="flex items-center space-x-2">
          <Music2 className="h-5 w-5 text-purple-300" />
          <h3 className="text-lg font-semibold text-white">{result.model}</h3>
          {result.durationSeconds !== null && (
            <span className="text-sm text-white/50">
              {t('music.result.duration', {
                defaultValue: '{{seconds}}s',
                seconds: String(result.durationSeconds),
              })}
            </span>
          )}
        </div>

        {/* HTML5 audio player */}
        <audio controls src={result.audioUrl} className="w-full" style={{ colorScheme: 'dark' }} />

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {onCreateVideoClip && (
            <button
              type="button"
              onClick={() => onCreateVideoClip(result)}
              className="btn-primary px-6 py-3 text-base font-semibold flex items-center space-x-2"
              data-testid="create-music-video-clip"
            >
              <Film className="h-5 w-5" />
              <span>
                {t('music.result.createVideoClip', { defaultValue: 'Create Music Video Clip' })}
              </span>
            </button>
          )}

          {showOpenFolder ? (
            <button
              type="button"
              onClick={handleOpenFolder}
              className="btn-secondary px-6 py-3 text-base font-semibold flex items-center space-x-2"
              data-testid="open-folder-button"
            >
              <FolderOpen className="h-5 w-5" />
              <span>{t('result.buttons.showInFinder')}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className="btn-secondary px-6 py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Download className="h-5 w-5" />
              <span>
                {isDownloading
                  ? t('common.downloading', { defaultValue: 'Downloading...' })
                  : t('music.result.download', { defaultValue: 'Download MP3' })}
              </span>
            </button>
          )}

          <button
            type="button"
            onClick={onReset}
            className="btn-secondary px-6 py-3 text-base font-semibold flex items-center space-x-2"
          >
            <RotateCcw className="h-5 w-5" />
            <span>{t('music.result.again', { defaultValue: 'Generate again' })}</span>
          </button>
        </div>
      </div>

      {/* Timed lyrics */}
      {result.lyrics && (
        <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl p-4 sm:p-6 space-y-3">
          <h4 className="text-sm font-semibold text-white/70 uppercase tracking-wider">
            {t('music.result.lyricsTitle', { defaultValue: 'Timed lyrics' })}
          </h4>
          <pre className="text-white/80 text-sm font-mono whitespace-pre-wrap leading-relaxed">
            {result.lyrics}
          </pre>
        </div>
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
});

export default MusicResult;
